import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import WithSidemenuHeader from '../../Components/WithSidemenuHeader';
import SideMenu from '../../Components/SideMenu';
import OutboundService from '../../ApiServices/Outbound.Service';
import './InboundReview.css';
import { Link, NavLink } from "react-router-dom";
import InboundService from '../../ApiServices/Inbound.Service';
import { persistor } from '../../Redux/Store';
import { useSelector } from 'react-redux';
import { S3UploadQuestionnaire_Enterpriseapps, S3DownloadForInboundAttachments, S3DeleteInboundAttachments } from '../../ApiServices/S3Bucket';
// Comment interfaces for embedded comments
interface CommentModel {
    _id: string;
    comment: string;
    userId: string;
    commentType: 'outbound' | 'inbound';
    status: boolean;
    createdAt: string;
    updatedAt: string;
    user?: {
        _id: string;
        firstName?: string;
        lastName?: string;
    };
}

interface CreateCommentModel {
    questionId: string;
    comment: string;
    commentType: 'outbound' | 'inbound';
}

interface AttachmentProps {
    _id: string;
    fileName: string;
}

interface QuestionProps {
    question: string;
    answer: string;
    _id: string;
    attachments?: AttachmentProps[];
}

interface InboundResponse {
    _id: string;
    outboundId: string;
    dbName: string;
}

interface InboundData {
    _id: string;
    clientName: string;
    email: string;
    status: boolean;
    questions: QuestionProps[];
    createdAt: string;
    updatedAt: string;
}

function InboundReview() {
    const [answer, setAnswer] = useState('');
    const [answerError, setAnswerError] = useState('');
    const [selectedQuestionData, setSelectedQuestionData] = useState<QuestionProps>();
    const [questions, setQuestions] = useState<QuestionProps[]>([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [inboundData, setInboundData] = useState<InboundData | null>(null);
    const [inboundResponse, setInboundResponse] = useState<InboundResponse | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [questionnairesBucket, setQuestionnairesBucket] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    
    // Comment states
    const [comments, setComments] = useState<CommentModel[]>([]);
    const [newComment, setNewComment] = useState('');
    const [isAddingComment, setIsAddingComment] = useState(false);
    const [commentError, setCommentError] = useState('');
    
    // Filter state
    const [filter, setFilter] = useState<'all' | 'answered' | 'unanswered'>('all');
    
    // Auto-save tracking
    const lastSavedAnswerRef = useRef<string>('');
    const autoSaveIntervalRef = useRef<NodeJS.Timeout | null>(null);
    // Refs to access current state values in interval callback
    const answerRef = useRef<string>(answer);
    const selectedQuestionDataRef = useRef<QuestionProps | undefined>(selectedQuestionData);
    const inboundDataRef = useRef<InboundData | null>(inboundData);
    const inboundResponseRef = useRef<InboundResponse | null>(inboundResponse);
    const questionsRef = useRef<QuestionProps[]>(questions);
    const currentQuestionIndexRef = useRef<number>(currentQuestionIndex);
    // Capture setState functions (they're stable)
    const setQuestionsRef = useRef(setQuestions);
    const setSelectedQuestionDataRef = useRef(setSelectedQuestionData);
    
    const clientDetails = useSelector((state: any) => state.AppReducer.clientDetails);
    const navigate = useNavigate();
    const params = useParams();

    useEffect(() => {
        if (!params.id || params.id.trim() === '') {
            navigate('/login', { replace: true });
            return;
        }
        const storageKey = process.env.REACT_APP_STORAGE_KEY;
        localStorage.removeItem(`${storageKey}/returnUrl`);
        localStorage.setItem(`${storageKey}/lastInboundReviewUrl`, `/inbound/review/${params.id}`);
        getInboundById(params.id);
    }, [params.id, navigate]);

    // Update refs when state changes
    useEffect(() => {
        answerRef.current = answer;
    }, [answer]);

    useEffect(() => {
        selectedQuestionDataRef.current = selectedQuestionData;
    }, [selectedQuestionData]);

    useEffect(() => {
        inboundDataRef.current = inboundData;
    }, [inboundData]);

    useEffect(() => {
        inboundResponseRef.current = inboundResponse;
    }, [inboundResponse]);

    useEffect(() => {
        questionsRef.current = questions;
    }, [questions]);

    useEffect(() => {
        currentQuestionIndexRef.current = currentQuestionIndex;
    }, [currentQuestionIndex]);

    // Update setState function refs (they're stable but we capture them)
    useEffect(() => {
        setQuestionsRef.current = setQuestions;
        setSelectedQuestionDataRef.current = setSelectedQuestionData;
    });

    useEffect(() => {
        if (questions.length > 0) {
            setSelectedQuestionData(questions[currentQuestionIndex]);
            const currentAnswer = questions[currentQuestionIndex].answer || '';
            setAnswer(currentAnswer);
            // Update the last saved answer ref when question changes
            lastSavedAnswerRef.current = currentAnswer;
            // Load comments for the selected question
            if (questions[currentQuestionIndex]._id) {
                loadComments(questions[currentQuestionIndex]._id);
            }
        }
    }, [currentQuestionIndex, questions]);

    // Auto-save effect: save answer every 10 seconds
    useEffect(() => {
        // Clear any existing interval
        if (autoSaveIntervalRef.current) {
            clearInterval(autoSaveIntervalRef.current);
        }

        // Only set up auto-save if we have the necessary data
        if (selectedQuestionDataRef.current?._id && inboundDataRef.current?._id && inboundResponseRef.current?.dbName) {
            console.log('Setting up auto-save interval');
            // Set up interval to auto-save every 10 seconds
            // Use refs to access current values in the interval callback
            autoSaveIntervalRef.current = setInterval(async () => {
                const currentAnswer = answerRef.current;
                const currentQuestionData = selectedQuestionDataRef.current;
                const currentInboundData = inboundDataRef.current;
                const currentInboundResponse = inboundResponseRef.current;

                // Skip if answer is empty or hasn't changed
                if (!currentAnswer || currentAnswer === lastSavedAnswerRef.current) {
                    console.log('Auto-save skipped: answer empty or unchanged');
                    return;
                }

                // Skip if required data is missing
                if (!currentQuestionData?._id || !currentInboundData?._id) {
                    console.log('Auto-save skipped: missing question or inbound data');
                    return;
                }

                try {
                    console.log('Auto-saving answer:', currentAnswer);
                    const result = await OutboundService.updateOutboundAnswer({
                        outboundId: currentInboundData._id,
                        questionId: currentQuestionData._id,
                        answer: currentAnswer,
                        dbName: currentInboundResponse?.dbName
                    });

                    if (result && result.status === true) {
                        // Update the last saved answer ref
                        lastSavedAnswerRef.current = currentAnswer;
                        // Update local state to keep UI in sync
                        const currentQuestions = questionsRef.current;
                        const currentIndex = currentQuestionIndexRef.current;
                        if (currentQuestions && currentIndex !== undefined) {
                            const updatedQuestions = [...currentQuestions];
                            if (updatedQuestions[currentIndex]) {
                                updatedQuestions[currentIndex] = {
                                    ...updatedQuestions[currentIndex],
                                    answer: currentAnswer
                                };
                                setQuestionsRef.current(updatedQuestions);
                                if (selectedQuestionDataRef.current) {
                                    setSelectedQuestionDataRef.current({
                                        ...selectedQuestionDataRef.current,
                                        answer: currentAnswer
                                    });
                                }
                            }
                        }
                        console.log('Auto-save successful');
                    }
                } catch (error) {
                    console.error('Error auto-saving answer:', error);
                }
            }, 5000); // 1 second for testing
        } else {
            console.log('Auto-save interval NOT set up - missing data:', {
                hasSelectedQuestion: !!selectedQuestionDataRef.current?._id,
                hasInboundData: !!inboundDataRef.current?._id,
                hasInboundResponse: !!inboundResponseRef.current?.dbName
            });
        }

        // Cleanup interval on unmount or when dependencies change
        return () => {
            if (autoSaveIntervalRef.current) {
                clearInterval(autoSaveIntervalRef.current);
                autoSaveIntervalRef.current = null;
            }
        };
    }, [selectedQuestionData?._id, inboundData?._id, inboundResponse?.dbName]);

    const getInboundQuestions = async (id: string, dbName: string) => {
        try {
            setIsLoading(true);
            const result = await OutboundService.getOutboundQuestions(id, dbName);
            if (result && result.status === true && result.data) {
                
                const inboundData = result.data;
                setInboundData(inboundData);
                setQuestionnairesBucket(result.questionnairesBucket);
                const questionsList = inboundData.questions || [];
                setQuestions(questionsList);
                if (questionsList.length > 0) {
                    setSelectedQuestionData(questionsList[0]);
                    setAnswer(questionsList[0].answer || '');
                    // Load comments for the first question
                    loadComments(questionsList[0]._id);
                }
            }
        } catch (error) {
            console.error('Error fetching questions:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const loadComments = async (questionId: string) => {
        try {
            console.log('InboundReview - Loading comments for questionId:', questionId);
            console.log('InboundReview - Current dbName:', inboundResponse?.dbName);
            
            // Use the database name from inboundResponse instead of localStorage
            const cleanDbName = inboundResponse?.dbName ? inboundResponse.dbName.replace(/"/g, '').trim() : undefined;
            console.log('InboundReview - Cleaned dbName for loading comments:', cleanDbName);
            if (!inboundResponse?.outboundId) {
                console.error('InboundReview - No outbound ID available for loading comments');
                setComments([]);
                return;
            }
            const result = cleanDbName 
                ? await OutboundService.getCommentsForQuestion(questionId, inboundResponse.outboundId, cleanDbName)
                : await OutboundService.getCommentsForQuestion(questionId, inboundResponse.outboundId);
            console.log('InboundReview - Comments result:', result);
            if (result && result.status === true && result.data) {
                setComments(result.data);
            } else {
                setComments([]);
            }
        } catch (error) {
            console.error('Error loading comments:', error);
            setComments([]);
        }
    };

    const getInboundById = async (id: string) => {
        try {
            const response = await InboundService.getInboundById(id);
            console.log(response, "getInboundById")
            if (response && response.status) {
                setInboundResponse(response.data);
                if (response.data.outboundId) {
                    getInboundQuestions(response.data.outboundId, response.data.dbName);
                }
            }
        } catch (error) {
            console.error('Error fetching inbound:', error);
        }
    };

    const onUpdateAnswer = async (isAutoSave: boolean = false) => {
        // For auto-save, skip if answer is empty or hasn't changed
        if (isAutoSave) {
            if (!answer || answer === lastSavedAnswerRef.current) {
                return;
            }
        } else {
            // For manual save, show error if answer is empty
            if (!answer) {
                return setAnswerError('Answer field is required');
            }
        }
        
        if (!selectedQuestionData?._id || !inboundData?._id) {
            if (!isAutoSave) {
                return setAnswerError('Question ID or Inbound ID is missing');
            }
            return;
        }
        
        if (!isAutoSave) {
            setAnswerError('');
        }

        try {
            console.log(answer, "answer");
            const result = await OutboundService.updateOutboundAnswer({
                outboundId: inboundData._id,
                questionId: selectedQuestionData._id,
                answer: answer,
                dbName: inboundResponse?.dbName
            });

            if (result && result.status === true) {
                // Update local state immediately without global loading
                const updatedQuestions = [...questions];
                updatedQuestions[currentQuestionIndex] = {
                    ...updatedQuestions[currentQuestionIndex],
                    answer: answer
                };
                setQuestions(updatedQuestions);
                setSelectedQuestionData({
                    ...selectedQuestionData,
                    answer: answer
                });
                // Update the last saved answer ref
                lastSavedAnswerRef.current = answer;
            }
        } catch (error) {
            console.error('Error updating answer:', error);
            if (!isAutoSave) {
                setAnswerError('Failed to update answer');
            }
        }
    };

    const handleQuestionSelect = (index: number) => {
        setCurrentQuestionIndex(index);
        // Clear comments when switching questions
        setComments([]);
    };

    // Get filtered questions with their original indices
    const getFilteredQuestions = () => {
        return questions
            .map((question, originalIndex) => ({ question, originalIndex }))
            .filter(({ question }) => {
                if (filter === 'all') return true;
                if (filter === 'answered') return question.answer && question.answer.trim() !== '';
                if (filter === 'unanswered') return !question.answer || question.answer.trim() === '';
                return true;
            });
    };

    const handleAddComment = async () => {
        if (!newComment.trim()) {
            setCommentError('Comment cannot be empty');
            return;
        }
        if (!selectedQuestionData?._id) {
            setCommentError('Question ID is missing');
            return;
        }
        
        setCommentError('');
        setIsAddingComment(true);
        
        try {
            const commentData: CreateCommentModel = {
                questionId: selectedQuestionData._id,
                comment: newComment.trim(),
                commentType: 'inbound'
            };
            
            console.log('InboundReview - Sending comment data:', commentData);
            console.log('InboundReview - Current dbName:', inboundResponse?.dbName);
            console.log('InboundReview - inboundResponse object:', inboundResponse);
            
            // Use the database name from inboundResponse instead of localStorage
            const cleanDbName = inboundResponse?.dbName ? inboundResponse.dbName.replace(/"/g, '').trim() : undefined;
            console.log('InboundReview - Cleaned dbName for comment creation:', cleanDbName);
            console.log('InboundReview - Will send dbName to API:', cleanDbName);
            if (!inboundResponse?.outboundId) {
                console.error('InboundReview - No outbound ID available for comment creation');
                setCommentError('Failed to add comment - no outbound ID');
                return;
            }
            const result = cleanDbName 
                ? await OutboundService.createComment(commentData, inboundResponse.outboundId, cleanDbName)
                : await OutboundService.createComment(commentData, inboundResponse.outboundId);
            console.log('InboundReview - Comment creation result:', result);
            
            if (result && result.status === true) {
                setNewComment('');
                // Reload comments to show the new one
                await loadComments(selectedQuestionData._id);
            } else {
                console.error('InboundReview - Comment creation failed:', result);
                setCommentError('Failed to add comment');
            }
        } catch (error) {
            console.error('Error adding comment:', error);
            setCommentError('Failed to add comment');
        } finally {
            setIsAddingComment(false);
        }
    };

    const onFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            setUploadError('');
            // Automatically upload the file when selected
            await handleAddAttachment(file);
        }
    };

    const handleAddAttachment = async (file?: File) => {
        const fileToUpload = file || selectedFile;
        if (!fileToUpload) {
            setUploadError('Please select a file first');
            return;
        }
        if (!selectedQuestionData?._id || !inboundData?._id) {
            setUploadError('Question ID or Inbound ID is missing');
            return;
        }
        const fileName = fileToUpload.name;
        //get file name
        //save file name to db
        const result = await OutboundService.updateOutboundAttachment({
            outboundId: inboundData._id,
            questionId: selectedQuestionData._id,
            fileName: fileName,
            dbName: inboundResponse?.dbName
        });

        console.log(result, "result");

        // setIsUploading(true);
        // setUploadError('');

        try {
            // Upload to S3
            console.log(clientDetails, "clientDetails")
            const uploadResult = await S3UploadQuestionnaire_Enterpriseapps({ 
                file: fileToUpload, 
                fileId: result.data.insertedAttachment._id, 
                bucketName: result.data.questionnairesBucket, 
                folderName: "inboundAttachments" 
            });

            if (uploadResult && (uploadResult as any).status === 200) {
                setSelectedFile(null);
                // Reset file input
                const fileInput = document.getElementById('inbound-attachment-file') as HTMLInputElement;
                if (fileInput) {
                    fileInput.value = '';
                }
                
                // Update local state to include the new attachment
                const updatedQuestions = [...questions];
                const currentQuestion = updatedQuestions[currentQuestionIndex];
                if (!currentQuestion.attachments) {
                    currentQuestion.attachments = [];
                }
                currentQuestion.attachments.push({
                    _id: result.data.insertedAttachment._id,
                    fileName: fileName
                });
                setQuestions(updatedQuestions);
                setSelectedQuestionData({
                    ...selectedQuestionData,
                    attachments: currentQuestion.attachments
                });
                
                console.log('File uploaded successfully');
            } else {
                setUploadError('Failed to upload file');
            }
        } catch (error) {
            console.error('Error uploading file:', error);
            setUploadError('Failed to upload file');
        } finally {
            setIsUploading(false);
        }
    };

    const handleDeleteAttachment = async (attachmentId: string) => {
        if (!selectedQuestionData?._id || !inboundData?._id) {
            return;
        }
        
        // Find the attachment to get its details
        const attachment = selectedQuestionData.attachments?.find(att => att._id === attachmentId);
        if (!attachment) {
            return;
        }
        
        try {
            setIsDeleting(true);
            
            // Delete from S3 first
            // const s3Result = await S3DeleteInboundAttachments({
            //     bucketName: questionnairesBucket ,
            //     fileName: attachment.fileName,
            //     fileId: attachment._id,
            //     folderName: "inboundAttachments"
            // }); 

            // if (s3Result && (s3Result as any).status === 200) {
                // Then delete from database
                const result = await OutboundService.deleteOutboundAttachment({
                    outboundId: inboundData._id,
                    questionId: selectedQuestionData._id,
                    attachmentId: attachmentId,
                    dbName: inboundResponse?.dbName
                });

                if (result && result.status === true) {
                    // Update local state to remove the deleted attachment
                    const updatedQuestions = [...questions];
                    const currentQuestion = updatedQuestions[currentQuestionIndex];
                    if (currentQuestion.attachments) {
                        currentQuestion.attachments = currentQuestion.attachments.filter(
                            attachment => attachment._id !== attachmentId
                        );
                    }
                    setQuestions(updatedQuestions);
                    setSelectedQuestionData({
                        ...selectedQuestionData,
                        attachments: currentQuestion.attachments
                    });
                }
            // }
        } catch (error) {
            console.error('Error deleting attachment:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDownloadAttachment = async (attachment: AttachmentProps) => {
        try {
            setIsDownloading(true);
            console.log(questionnairesBucket, "questionnairesBuckets")
            // Use S3 download function with client details from Redux
            const result = await S3DownloadForInboundAttachments({
                bucketName: questionnairesBucket ,
                fileName: attachment.fileName,
                fileId: attachment._id,
                folderName: "inboundAttachments"
            });

            if (result && (result as any).status === 200) {
                console.log('File downloaded successfully');
            } else {
                throw new Error('Failed to download file');
            }
        } catch (error) {
            console.error('Error downloading attachment:', error);
        } finally {
            setIsDownloading(false);
        }
    };

    const logout = async () => {
        const id = params.id?.trim();
        localStorage.clear();
        if (id) {
            localStorage.setItem(
                `${process.env.REACT_APP_STORAGE_KEY}/returnUrl`,
                `/inbound/review/${id}`
            );
        }
        persistor.purge().then(async () => {
            window.location.href = '/login';
            await persistor.purge();
        });
    }

    return (
        <main className="acc-main">
            <section className="left-menu-col">
            <div className="left-menu-main">
            <div className="left-menu">
                <div className="logo">
                    <img src={process.env.PUBLIC_URL + '/assets/imgs/targhee-logo.png'} className='img-fluid' alt="logo" />
                </div>

                <div className="menu-links">
                    <ul className='list-unstyled'>
                       
                        {/* <li>
                            <NavLink to="/help-center" className={({ isActive }) => (isActive ? "menu-active" : "")}>Help Center</NavLink>
                        </li>
                        <li>
                            <NavLink to="/api-guide" className={({ isActive }) => (isActive ? "menu-active" : "")}>API Guide</NavLink>
                        </li> */}
                    </ul>
                </div>

                <div className='footre-lnks'>
                    <Link to="/terms-of-service" target='_blank'>Terms of Service</Link>
                    <Link to="/privacy-policy" target='_blank'>Privacy Policy</Link>
                    <p>Copyrights 2025 Targhee Security</p>
                </div>
            </div>
        </div>
            </section>
            <section className="right-routers-col">
            <>
      <div className='header-details QQ'>
        <div>
          <h5 className='fw-bold'>{  'Dashboard'}</h5>
        </div>
        <div className="spacer"></div>
        <div>
          <button 
            onClick={logout}
            className="btn btn-outline-danger"
          >
            Logout
          </button>
        </div>
      </div>
      <div className="spacer"></div>
      <div>

      </div>

    </>
                <div className="router-container">
              
                    <div className="main-container">
                        {isLoading ? (
                            <div className="text-center">Loading...</div>
                        ) : selectedQuestionData ? (
                            <>
                                <div className="account-container">
                                    <div className="account-name-container">
                                        <h3 className="account-heading">{inboundData?.clientName}</h3>
                                        <p>{inboundData?.email}</p>
                                    </div>
                                    <div className="account-progress-container">
                                        <h3>Progress</h3>
                                        <div className="progress-bar-container">
                                            <div 
                                                className="progress-bar" 
                                                style={{ 
                                                    width: `${(questions.filter(q => q.answer).length / questions.length) * 100}%` 
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="row mt-4">
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <div className="d-flex gap-2 align-items-center">
                                                
                                                <button
                                                    className={`btn btn-sm ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                    onClick={() => setFilter('all')}
                                                >
                                                    All
                                                </button>
                                                <button
                                                    className={`btn btn-sm ${filter === 'answered' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                    onClick={() => setFilter('answered')}
                                                >
                                                    Answered
                                                </button>
                                                <button
                                                    className={`btn btn-sm ${filter === 'unanswered' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                    onClick={() => setFilter('unanswered')}
                                                >
                                                    Unanswered
                                                </button>
                                            </div>
                                        </div>
                                        <div className="outbound-questions-lists">
                                            {getFilteredQuestions().map(({ question, originalIndex }, filteredIndex) => (
                                                <div
                                                    key={question._id}
                                                    className={`outbound-question-item${currentQuestionIndex === originalIndex ? ' outbound-question-item-active' : ''}`}
                                                    onClick={() => handleQuestionSelect(originalIndex)}
                                                >
                                                    <p className={currentQuestionIndex === originalIndex ? 'outbound-question-text-white' : 'outbound-question-text'}>
                                                        {question.question}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="outbound-answers-container">
                                            <h3>Question {currentQuestionIndex + 1} of {questions.length}</h3>
                                            <div className="outbound-question-text mb-4">
                                                {selectedQuestionData.question}
                                            </div>
                                            <textarea 
                                                rows={6}
                                                value={answer}
                                                onChange={(e) => setAnswer(e.target.value)}
                                                className="answer-textarea"
                                                placeholder="Enter your answer here..."
                                            />
                                            {answerError && (
                                                <div className="error-message">{answerError}</div>
                                            )}
                                            <div className="d-flex gap-3 mt-3">
                                                <button 
                                                    onClick={() => onUpdateAnswer(false)}
                                                    className="save-comment"
                                                >
                                                    Save Answer
                                                </button>
                                                <input 
                                                    id="inbound-attachment-file" 
                                                    type="file" 
                                                    accept="application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/*,.doc,.docx" 
                                                    onChange={onFileUpload} 
                                                    style={{ display: 'none' }} 
                                                />
                                                <button 
                                                    type="button"
                                                    onClick={() => document.getElementById('inbound-attachment-file')?.click()}
                                                    className="btn btn-outline-primary"
                                                    disabled={isUploading}
                                                >
                                                    {isUploading ? 'Uploading...' : 'Add Attachment'}
                                                </button>
                                            </div>
                                            {uploadError && (
                                                <div className="error-message mt-2">{uploadError}</div>
                                            )}
                                            
                                            {/* File List */}
                                            {selectedQuestionData?.attachments && selectedQuestionData.attachments.length > 0 && (
                                                <div className="mt-4">
                                                    <h5>Attachments</h5>
                                                    <div className="attachment-list">
                                                        {selectedQuestionData.attachments.map((attachment) => (
                                                            <div key={attachment._id} className="attachment-item">
                                                                <div className="attachment-info">
                                                                    <span className="attachment-name">{attachment.fileName}</span>
                                                                </div>
                                                                <div className="attachment-actions">
                                                                    <button
                                                                        onClick={() => handleDownloadAttachment(attachment)}
                                                                        disabled={isDownloading}
                                                                        className="btn btn-sm btn-outline-primary me-2"
                                                                    >
                                                                        {isDownloading ? 'Downloading...' : 'Download'}
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleDeleteAttachment(attachment._id)}
                                                                        disabled={isDeleting}
                                                                        className="btn btn-sm btn-outline-danger"
                                                                    >
                                                                        {isDeleting ? 'Deleting...' : 'Delete'}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Comments Section */}
                                            <div className="mt-4">
                                                <h5>Comments</h5>
                                                <div className="comments-section">
                                                    {/* Add Comment */}
                                                    <div className="add-comment-section mb-3">
                                                        <textarea
                                                            value={newComment}
                                                            onChange={(e) => setNewComment(e.target.value)}
                                                            className="form-control"
                                                            rows={3}
                                                            placeholder="Add a comment..."
                                                            disabled={isAddingComment}
                                                        />
                                                        {commentError && (
                                                            <div className="error-message mt-2">{commentError}</div>
                                                        )}
                                                        <button
                                                            onClick={handleAddComment}
                                                            disabled={isAddingComment || !newComment.trim()}
                                                            className="btn btn-primary mt-2"
                                                        >
                                                            {isAddingComment ? 'Adding...' : 'Add Comment'}
                                                        </button>
                                                    </div>

                                                    {/* Comments Thread */}
                                                    <div className="comments-thread">
                                                        {comments.length > 0 ? (
                                                            comments.map((comment) => (
                                                                <div key={comment._id} className="comment-item">
                                                                    <div className="comment-header">
                                                                        <span className="comment-author">
                                                                            {comment.user?.firstName} {comment.user?.lastName}
                                                                        </span>
                                                                        <span className={`comment-type ${comment.commentType === 'inbound' ? 'inbound' : ''}`}>
                                                                            {comment.commentType === 'outbound' ? inboundResponse?.dbName || 'Outbound' : inboundData?.clientName || 'Inbound'}
                                                                        </span>

                                                                    </div>
                                                                    <div className="comment-content">
                                                                        {comment.comment}
                                                                    </div>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div className="no-comments">
                                                                No comments yet. Be the first to add one!
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                                              
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="text-center">
                                No questions found
                                {questions.length === 0 && !isLoading && (
                                    <div className="mt-2 text-muted">
                                        Please check if the inbound ID is correct
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </main>
    );
}

export default InboundReview; 